<template>
  <layout-auth :width="600" :center-vertically="false">
    <!--Basic Info-->
    <register-section heading="Your Info">
      <form-field-input
        label="Name"
        class="column is-6"
        placeholder="First Name"
        v-model="registrationForm.first_name"
        :validation-errors="validationErrors.first_name"
        autofocus
      ></form-field-input>

      <form-field-input
        class="column is-6"
        style="margin-top: 22px"
        placeholder="Last Name"
        v-model="registrationForm.last_name"
        :validation-errors="validationErrors.last_name"
      ></form-field-input>

      <form-field-phone
        class="column is-full"
        label="Phone Number"
        v-model="registrationForm.phone_number"
        :validation-errors="validationErrors.phone_number"
      ></form-field-phone>

      <form-field-input
        class="column is-6"
        label="Email Address"
        type="email"
        v-model="registrationForm.email_address"
        :validation-errors="validationErrors.email_address"
      ></form-field-input>

      <form-field-input
        class="column is-6"
        label="Confirm Email Address"
        type="email"
        v-model="registrationForm.email_address_confirmation"
      ></form-field-input>

      <form-field-password
        class="column is-full"
        label="Choose a Password"
        help-text="password must have at least 8 characters"
        v-model="registrationForm.password"
        :validation-errors="validationErrors.password"
      ></form-field-password>
    </register-section>

    <!--Address-->
    <register-section heading="Company Info">
      <form-field-input
        class="column is-full"
        label="Company Name"
        v-model="registrationForm.company_name"
        :validation-errors="validationErrors.company_name"
      ></form-field-input>

      <form-field-input
        class="column is-full"
        label="Street Address"
        v-model="registrationForm.street_address"
        :validation-errors="validationErrors.street_address"
      ></form-field-input>

      <form-field-input
        class="column is-8"
        label="City"
        v-model="registrationForm.city"
        :validation-errors="validationErrors.city"
      ></form-field-input>

      <form-field-select
        class="column is-4"
        label="State"
        v-model="registrationForm.state"
        :validation-errors="validationErrors.state"
      >
        <option></option>
        <option
          v-for="state in usStates"
          :key="state.abbreviation"
          :value="state.abbreviation"
        >{{ state.abbreviation }}
        </option>
      </form-field-select>

      <form-field-input
        class="column is-5"
        label="Postcode"
        v-model="registrationForm.postcode"
        :validation-errors="validationErrors.postcode"
      ></form-field-input>

      <form-field-timezones
        class="column is-7"
        label="Timezone"
        :entity="registrationForm"
        :validation-errors="validationErrors"
      ></form-field-timezones>
    </register-section>

    <!--Payment-->
    <register-section heading="Credit Card" hide-columns>
      <form-field-input
        label="Name on Card"
        v-model="registrationForm.nameOnCard"
        :validation-errors="validationErrors.nameOnCard"
      ></form-field-input>

      <app-stripe-element
        ref="stripeElement"
        @postalChanged="updateStateTaxable"
      ></app-stripe-element>
    </register-section>

    <!--Payment Summary-->
    <register-section
      v-if="webinarSetup"
      heading="Webinar Special"
      hide-columns
    >
      <ul class="webinar_offer">
        <li>Get 90 Days of Forefront for free. After that you'll be charged $199 per month (plus applicable tax).</li>
        <li>White Glove Implementation & Set-Up Service</li>
        <li>Automated Cross Channel Conversion Campaigns</li>
        <li>Live Acquisitions Call Training & Coaching (Bi-monthly)</li>
        <li>On-Demand Workshop - 5 Steps to Crush Cold Calling</li>
        <li>On-Demand Workshop - Direct Mail Hacks Masterclass</li>
        <li>On-Demand Workshop - Bulletproof Strategies For Finding Private Money</li>
      </ul>

      <div class="total_line large">
        <p class="total_line--label">Due Today:</p>
        <p class="total_line--value">$497</p>
      </div>

      <p class="disclaimer_text mt-100">
        After the initial period you will be charged every {{ isMonthlyCycle ? "month" : "year" }}. You can cancel
        anytime inside Forefront via your account modal. If you cancel your account you will still have access to Forefront until the end of your current billing period.
      </p>
    </register-section>

    <register-section
      v-else
      heading="Payment Summary"
      hide-columns
    >
      <div class="cost_buttons">
        <button-link
          @click="openChangePlanModal"
        >change plan</button-link>

        <button-link
          class="ml-100"
          @click="openCouponCodeModal"
        >apply coupon</button-link>
      </div>

      <div class="cost_line">
        <p>{{ planName }}</p>
        <p>${{ cost }}</p>
      </div>

      <div class="cost_line">
        <p>Tax <small>({{ taxRate }}%)</small></p>
        <p>${{ taxAmount }}</p>
      </div>

      <template v-if="couponInfo">
        <p
          v-if="isAnnualCycle"
          class="coupon--annual_message"
        >Coupon codes can't be combined with the annual discount. Please choose a monthly plan to take advantage of your
          coupon code {{ couponInfo.name }}</p>

        <template v-else>
          <div class="cost_line">
            <p>Coupon: {{ couponInfo.name }}</p>
            <p>
              <template v-if="couponInfo.type === ENUMS.CouponTypeEnum.FixedAmount.value">${{ couponInfo.amount_off }}
              </template>
              <template v-if="couponInfo.type === ENUMS.CouponTypeEnum.Percentage.value">{{ couponInfo.percent_off }}%
              </template>
              <template v-if="couponInfo.duration === ENUMS.CouponDurationEnum.Once.value"> off first {{
                  isAnnualCycle ? 'year' : 'month'
                }}
              </template>
              <template v-if="couponInfo.duration === ENUMS.CouponDurationEnum.Repeating.value">off for {{
                  couponInfo.duration_in_months
                }} months
              </template>
              <template v-if="couponInfo.duration === ENUMS.CouponDurationEnum.Forever.value"> off forever</template>
            </p>
          </div>
          <p v-if="couponInfo.description" class="coupon_description">{{ couponInfo.description }}</p>
        </template>
      </template>

      <div class="total_line">
        <p class="total_line--label">Due Today:</p>
        <p class="total_line--value">${{ totalAmount }}</p>
      </div>

      <p class="disclaimer_text">
        You will be charged every {{ isMonthlyCycle ? "month" : "year" }}. You can cancel anytime inside Forefront via
        your account modal. If you cancel your account you will still have access to Forefront until the end of your
        current billing period.
      </p>
    </register-section>

    <!--Button-->
    <div class="button_section">
      <p v-if="creatingAccount" class="creating_text">Please wait while we provision your account, this may take a
        minute or two.</p>
      <p v-if="issueWithCard" class="creating_text has-text-danger">There was an issue creating your account.<br>Please contact support.</p>

      <button-default
        size=""
        class="is-fullwidth"
        @click="createAccount"
        :loading="creatingAccount"
      >Create Account
      </button-default>
    </div>

    <b-loading class="app-loader" :active="isLoading" :is-full-page="false"></b-loading>

    <modal-container>
      <modal-default
        title=""
        ref="changeBillingPlanModal"
        is-triple-column
      >
        <change-plan-modal
          :billing-plans="billingPlans"
          :current-plan="registrationForm.billing_plan"
          :current-cycle="registrationForm.billing_cycle"
          @selected="changePlan"
        ></change-plan-modal>
      </modal-default>

      <modal-default
        title="Coupon"
        ref="applyCouponCodeModal"
      >
        <form-field-input
          label="Coupon"
          v-model="registrationForm.promo_code"
          size=""
        ></form-field-input>

        <p v-if="showCouponError" class="coupon_modal--error">Coupon not valid.</p>

        <p class="coupon_modal--note">NOTE: Only one coupon code may be used at a time and coupons may not be used with the annual discount.</p>

        <button-default
          class="auth_button"
          size=""
          @click="getCouponInfoFromPromo"
          :loading="couponLoading"
          :disabled="couponLoading"
        >Apply Coupon</button-default>
      </modal-default>
    </modal-container>
  </layout-auth>
</template>

<script>
import auth from "../../api/auth";
import {usStates} from "../../services/staticData";
import {ENUMS} from "../../services/enums";
import LocalStorage from "../../services/localStorage";
import RegisterSection from "./components/RegisterSection";

/*
 * Testing Info
 *
 * test coupon_code = eiH5B8J9
 * test promo_code = CYBER75
 *
 * url string: ?plan=starter&cycle=annual&promo=cyber75&coupon=eiH5B8J9
 */

export default {
  name: "RegisterPage",
  components: {RegisterSection},
  metaInfo: {
    title: "Register",
    script: [
      {src: "https://cdn.firstpromoter.com/fpr.js", async: true}
    ],
  },
  data() {
    return {
      isLoading: true,
      couponLoading: false,
      creatingAccount: false,
      issueWithCard: false,
      showCouponError: false,
      webinarSetup: false,
      errorCount: 0,
      usStates,
      billingPlans: [],
      taxableStates: [],
      couponInfo: null,
      registrationForm: {
        first_name: null,
        last_name: null,

        email_address: null,
        email_address_confirmation: null,
        password: null,

        company_name: null,
        phone_number: null,

        street_address: null,
        city: null,
        state: null,
        postcode: null,
        timezone: "America/Chicago",

        billing_special: null,
        billing_plan: ENUMS.BillingPlanEnum.Pro.value,
        billing_cycle: ENUMS.BillingCycleEnum.Monthly.value,
        billing_state: null,

        paymentMethodId: null,

        coupon_code: null,
        promo_code: null,
        affiliate_id: null,

        tax_rate: 0,
        nameOnCard: null,
      },
      validationErrors: {
        first_name: null,
        last_name: null,

        email_address: null,
        password: null,

        company_name: null,
        phone_number: null,

        street_address: null,
        city: null,
        state: null,
        postcode: null,
        timezone: null,

        nameOnCard: null,
      }
    };
  },
  computed: {
    isMonthlyCycle() {
      return this.registrationForm.billing_cycle === ENUMS.BillingCycleEnum.Monthly.value;
    },
    isAnnualCycle() {
      return this.registrationForm.billing_cycle === ENUMS.BillingCycleEnum.Annual.value;
    },
    currentPlanInfo() {
      return this.billingPlans.find(plan => plan.type === this.registrationForm.billing_plan);
    },
    cost() {
      let cost = 0;

      if (this.isMonthlyCycle) cost = this.currentPlanInfo.monthly_cost_per_billing_period;
      if (this.isAnnualCycle) cost = this.currentPlanInfo.annual_cost_per_billing_period;

      return cost.toFixed(2);
    },
    taxRate() {
      return this.registrationForm.tax_rate;
    },
    taxAmount() {
      const taxAmount = this.cost * (this.taxRate / 100);
      return taxAmount.toFixed(2);
    },
    totalAmount() {
      let cost = 0;

      if (this.isMonthlyCycle) cost = this.currentPlanInfo.monthly_cost_per_billing_period;
      if (this.isAnnualCycle) cost = this.currentPlanInfo.annual_cost_per_billing_period;

      // Only apply promo coupons to monthly plans
      if (this.couponInfo && this.isMonthlyCycle) {
        if (this.couponInfo.type === ENUMS.CouponTypeEnum.FixedAmount.value) {
          cost = cost - this.couponInfo.amount_off
        }

        if (this.couponInfo.type === ENUMS.CouponTypeEnum.Percentage.value) {
          const discount = cost * (this.couponInfo.percent_off / 100)
          cost = cost - discount
        }
      }

      const taxAmount = this.cost * (this.taxRate / 100);

      return (taxAmount + cost).toFixed(2);
    },
    planName() {
      if(!this.currentPlanInfo) return '';
      if (this.isMonthlyCycle) return 'Monthly ' + this.currentPlanInfo.name;
      if (this.isAnnualCycle) return 'Annual ' + this.currentPlanInfo.name;
    },
  },
  mounted() {
    this.getBillingInfo();
    this.processQueryString();

    // First Promoter Affiliate Marketing Code
    if (process.env.MIX_FIRST_PROMOTER_KEY) {
      (function (w) {
        w.fpr = w.fpr || function () {
          w.fpr.q = w.fpr.q || [];
          w.fpr.q[arguments[0] == "set" ? "unshift" : "push"](arguments);
        };
      })(window);
      fpr("init", {cid: process.env.MIX_FIRST_PROMOTER_KEY});
      fpr("click");
    }
  },
  methods: {
    processQueryString() {
      // Set plan
      const queryPlan = this.$route.query.plan ? this.$route.query.plan.toLowerCase() : null;
      if (queryPlan && queryPlan === "starter") this.registrationForm.billing_plan = ENUMS.BillingPlanEnum.Starter.value;
      if (queryPlan && queryPlan === "pro") this.registrationForm.billing_plan = ENUMS.BillingPlanEnum.Pro.value;
      if (queryPlan && queryPlan === "elite") this.registrationForm.billing_plan = ENUMS.BillingPlanEnum.Elite.value;

      // set cycle
      const queryCycle = this.$route.query.cycle ? this.$route.query.cycle.toLowerCase() : null;
      if (queryCycle && queryCycle === "monthly") this.registrationForm.billing_cycle = ENUMS.BillingCycleEnum.Monthly.value;
      if (queryCycle && queryCycle === "annual") this.registrationForm.billing_cycle = ENUMS.BillingCycleEnum.Annual.value;

      // set coupon code
      const queryCoupon = this.$route.query.coupon;
      if (queryCoupon) {
        this.registrationForm.coupon_code = queryCoupon;
        this.getCouponInfoFromCoupon()

        const webinarCoupons = process.env.MIX_STRIPE_WEBINAR_COUPONS;

        if(webinarCoupons.includes(queryCoupon)) {
          this.webinarSetup = true
        }
      }

      // set promo code
      const queryPromo = this.$route.query.promo;
      if (queryPromo) {
        this.registrationForm.promo_code = queryPromo;
        this.getCouponInfoFromPromo()
      }
    },
    getBillingInfo() {
      this.isLoading = true;

      auth.billingInfo().then(response => {
        this.billingPlans = response.data.billing_plans;
        this.taxableStates = response.data.taxable_states;

        this.isLoading = false;
      })
    },
    openCouponCodeModal() {
      this.$refs.applyCouponCodeModal.open();
    },
    getCouponInfoFromCoupon() {
      this.couponLoading = true;
      this.showCouponError = false;

      auth.couponInfo(this.registrationForm.coupon_code, null).then(response => {
        this.couponInfo = response.data.coupon_info

        if(this.couponInfo) {
          this.$refs.applyCouponCodeModal.close()
        } else {
          this.showCouponError = true
        }
      }).finally(() => {
        this.couponLoading = false;
      })
    },
    getCouponInfoFromPromo() {
      this.couponLoading = true;
      this.showCouponError = false;

      auth.couponInfo(null, this.registrationForm.promo_code).then(response => {
        this.couponInfo = response.data.coupon_info

        if(this.couponInfo) {
          this.$refs.applyCouponCodeModal.close()
        } else {
          this.showCouponError = true
        }
      }).finally(() => {
        this.couponLoading = false;
      })
    },
    openChangePlanModal() {
      this.$refs.changeBillingPlanModal.open();
    },
    changePlan(newInfo) {
      this.registrationForm.billing_plan = newInfo.newPlan;
      this.registrationForm.billing_cycle = newInfo.newCycle;
    },
    updateStateTaxable(zipcode) {
      let state = this.$utils.getStateByZip(zipcode);

      if (state) {
        state = state.state;

        const taxableState = this.taxableStates.find(taxableState => taxableState.state === state);
        if (taxableState) this.registrationForm.tax_rate = parseInt(taxableState.tax_rate);

        this.registrationForm.billing_state = state;
      }
    },
    getPaymentMethod() {
      return new Promise((resolve, reject) => {
        const nameOnCard = this.registrationForm.nameOnCard;

        if (!nameOnCard) {
          this.validationErrors.nameOnCard = ["Name on card is required"];
          return;
        }

        this.validationErrors.nameOnCard = [];

        this.$refs.stripeElement.processStripePromise({name: nameOnCard}).then(response => {
          this.registrationForm.paymentMethodId = response.paymentMethodId;
          resolve();
        });
      });
    },
    createAccount() {
      this.creatingAccount = true;
      this.issueWithCard = false;

      // If they choose an annual plan then we need to remove the promo & coupon code from the form.
      // Our coupon codes are only for monthly plans.
      if (this.isAnnualCycle) {
        this.registrationForm.promo_code = null;
        this.registrationForm.coupon_code = null;
      }

      this.getPaymentMethod().then(() => {
        return auth.register(this.registrationForm)
          .then(res => {
            LocalStorage.setAuthUser(res.data.user);
            LocalStorage.setApiToken(res.data.api_token);
            LocalStorage.setAdminMode(res.data.is_admin);

            if (process.env.MIX_FIRST_PROMOTER_KEY && window.fpr) {
              window.fpr("referral", {email: res.data.user.email_address});
            }

            this.$router.push({name: "pipeline", query: {modal: 'welcome'}});
          })
          .catch(error => {
            this.validationErrors = error;
            // This will allow them to try to fix the error and retry. but dont want to toggle if success.  That will let the button be clickable before it redirects.
            this.issueWithCard = true;
            this.creatingAccount = false;
          });
      }).catch(() => {
        this.creatingAccount = false;
        this.issueWithCard = true;
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.cost_buttons {
  display: flex;
  align-items: center;
  margin: -6px 0 10px;
}

.cost_line {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 0.875rem;
}

.coupon--annual_message {
  margin-top: 10px;
  font-size: 0.875rem;
  font-style: italic;
  line-height: 1.3;
}

.coupon_description {
  margin-top: 4px;
  color: $text-light;
  font-size: 0.875rem;
}

.total_line {
  display: flex;
  justify-content: space-between;
  margin: 16px 0 10px;

  &.large {
    font-size: 1.25rem;
  }

  &--value {
    color: $secondary;
    font-weight: bold;
  }
}

.webinar_offer {
  padding-left: 1rem;
  line-height: 1.2;
  list-style: disc;

  li {
    margin-bottom: 8px;
    font-weight: bold;
    font-size: 0.875rem;
    color: $primary;
  }
}

.disclaimer_text {
  color: $text-muted;
  font-size: 0.875rem;
  line-height: 1.2;
}

.button_section {
  max-width: 480px;
  margin: -20px auto 20px;
  padding: 0 5%;
}

.creating_text {
  margin-top: 14px;
  margin-bottom: 10px;
  text-align: center;
  font-style: italic;
  line-height: 1.3;
}

.invalid_heading {
  margin-bottom: 12px;
  font-size: 1.375rem;
  font-weight: bold;
}

.invalid_text {
  line-height: 1.4;
}

.coupon_modal--note {
  margin-bottom: 12px;
  font-size: 0.875rem;
  line-height: 1.2;
  font-style: italic;
}

.coupon_modal--error {
  margin: -8px 0 12px;
  color: $danger;
}
</style>
